import { createGraphClient } from '~/client/graph'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopRootStore from '~/client/src/desktop/stores/DesktopRoot.store'
import registerDesktopEvents from '~/client/src/desktop/stores/EventStore/desktopEvents'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import Configuration, { IAppConfig } from '~/client/src/shared/Config'
import ApiAuthService from '~/client/src/shared/services/ApiAuthService'
import StruxHubAuthService from '~/client/src/shared/services/StruxHubAuthService'
import registerCommonEvents from '~/client/src/shared/stores/EventStore/commonEvents'
import EffectsProcessor from '~/client/src/shared/stores/EventStore/EffectsProcessors/EffectsProcessor'
import FlowProcessor from '~/client/src/shared/stores/EventStore/EffectsProcessors/FlowProcessor/FlowProcessor'

import CommonContainer from '../shared/CommonContainer'
import ProcoreService from '../shared/services/ProcoreService'
import GraphProcessor from '../shared/stores/EventStore/EffectsProcessors/GraphProcessors/GraphProcessor'
import WorkerProcessor from '../shared/stores/EventStore/EffectsProcessors/WorkerProcessor/WorkerProcessor'
import GraphExecutor from '../shared/utils/GraphExecutor'

export default class Container extends CommonContainer {
  private configuration: IAppConfig = Configuration
  private $rootStore: DesktopRootStore
  private initialState: DesktopInitialState
  protected eventsStore: DesktopEventStore
  private struxHubAuthService: StruxHubAuthService
  private apiAuthService: ApiAuthService
  private procoreService: ProcoreService
  private graphExecutor: GraphExecutor

  public constructor(private isTestEnv: boolean) {
    super()

    this.initialState = new DesktopInitialState()

    this.procoreService = new ProcoreService(this.initialState)

    this.graphExecutor = new GraphExecutor(this.initialState)

    this.apiAuthService = new ApiAuthService(
      this.initialState,
      this.graphExecutor,
    )

    this.struxHubAuthService = new StruxHubAuthService(this.apiAuthService)

    const getValidToken = () => this.struxHubAuthService.getValidAccessToken()
    const forceRefreshToken = () =>
      this.struxHubAuthService.refreshAccessToken()

    // Route REST refreshes (ApiAuthService.updateAccessToken) through the
    // GraphQL service's dedup gate so concurrent REST + GraphQL 401s share a
    // single network refresh instead of racing and revoking each other.
    this.apiAuthService.setRefreshAccessTokenDelegate(forceRefreshToken)

    // Create the graph client ONCE here with the proper URL. See the matching
    // change in mobile/container.ts for the full rationale; in short, replacing
    // state.graphClient mid-app-lifetime orphans React-Apollo hook
    // subscriptions, so we use one stable client and resolve auth per request
    // via getValidToken. forceRefreshToken backs Apollo's 401 retry path.
    this.initialState.graphClient = createGraphClient(
      getValidToken,
      forceRefreshToken,
      location.host,
      this.onSubscriptionsReconnected,
    )

    this.eventsStore = new DesktopEventStore(
      new EffectsProcessor(
        getValidToken,
        new FlowProcessor(),
        new WorkerProcessor(),
        new GraphProcessor(this.initialState, this.graphExecutor),
      ),
      this.initialState,
    )

    registerCommonEvents(this.eventsStore, this.rootStore)
    registerDesktopEvents(this.eventsStore, this.rootStore)

    this.registerGlobalEventListeners()
  }

  public get rootStore() {
    if (!this.$rootStore) {
      this.$rootStore = new DesktopRootStore(
        this.configuration,
        this.initialState,
        this.eventsStore,
        this.apiAuthService,
        this.struxHubAuthService,
        this.procoreService,
        this.graphExecutor,
        this.isTestEnv,
      )
    }

    return this.$rootStore
  }
}
